import {priceHelper} from '../../config-master.mjs';
import {convertJsDateToShortDate} from '../../common/date.mjs';
import {
  skuIsLoadedOrUnloadedVehicleSpecificEnclosure
} from '../../../helpers/products/vehicle-specific.mjs';

export const normalizeProductProviderDataForJsonLd = (product, reviewsFromDb) => {
  const isTrueVariantSku = product?.product_type === 'variant';
  // console.log("[normalizeProductProviderDataForJsonLd] isTrueVariantSku:", isTrueVariantSku);

  const variantParentSku = product?.variant_parent_sku ?? product?.variantParentSku;
  const variants = product?.variants;
  const relatedVariants = product?.relatedVariants;

  const productGroupName = product?.parentLevelSeoTitle ?? product?.websiteCollectionsRow?.collection_page_title;
  const productGroupDescription = product?.parentLevelSeoDescription ?? product?.websiteCollectionsRow?.collection_seo_description;
  const parentUrl = product?.websiteCollectionsRow?.handle ?
    `https://www.skaraudio.com/collections${product?.websiteCollectionsRow?.handle}`
    : null;

  const variesBy = product?.variesBy?.length ? product?.variesBy.map(item => {
    return mapVariesByToGoogleSchemaName[item] ?? null;
  })?.filter(Boolean) : null;

  const mainProductItem = {
    productGroupID: variantParentSku,
    name: productGroupName,
    description: productGroupDescription,
    url: parentUrl,
    variesBy: variesBy,
    rating: {
      count: reviewsFromDb?.count,
      avgRating: reviewsFromDb?.avgRating
    },
    products: variants?.map((variant) => {
      const matchingRelatedVariant = relatedVariants?.find(
        (relatedVariant) => relatedVariant?.sku === variant?.sku
      );

      const attributes = mapVariantOptionsToSchema(matchingRelatedVariant, product?.variesBy);

      return {
        sku: variant?.sku,
        upc: variant?.upc,
        name: matchingRelatedVariant?.seo_title ?? variant?.productTitle,
        description: matchingRelatedVariant?.seo_description ?? productGroupDescription,
        image: variant?.images?.[0]?.url,
        url: variant?.path ?? variant?.variantHandle,
        price: variant?.price,
        productType: variant?.product_type,
        attributes: attributes // Adding attributes here
      };
    })
  };
  // console.dir({mainProductItem}, {depth: 2});
  return mainProductItem;
};

export const generateItemWithVariantsJsonLd = (product, reviewsFromDb) => {
  const normalizedData = normalizeProductProviderDataForJsonLd(product, reviewsFromDb);
  // console.log('[generateItemWithVariantsJsonLd] normalizedData:', normalizedData);

  const priceValidUntil = generatePriceValidUntil();
  const variantProducts = normalizedData?.products?.map(product => {
    return generateVariantsJsonLd(product, priceValidUntil);
  });
  // console.dir({variantProducts}, {depth: 3});

  let jsonLdData = generateProductGroupJsonLd(normalizedData, variantProducts);
  jsonLdData = attachJsonLdShippingPolicyToJsonLdData(jsonLdData);
  jsonLdData = attachJsonLdReturnPolicyToJsonLdData(jsonLdData);
  // console.log('[generateItemWithVariantsJsonLd] jsonLdData:', jsonLdData);
  return jsonLdData;
};

const generateVariantsJsonLd = (variant, priceValidUntil) => {
  const variantUrl = `https://www.skaraudio.com${variant?.url}`;
  const currentPrice = variant?.price ?? priceHelper?.getFinalPrice(variant);
  const reviewsAggregateAvgScore = parseFloat(variant?.rating?.avgRating);
  const reviewsAggregateCount = variant?.rating?.count;
  const gtin12 = variant?.upc;

  const variantAttributes = variant?.attributes ?? {};
  // console.dir({variantAttributes}, {depth: 3});

  let variantSchema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    sku: variant?.sku,
    name: variant?.name,
    description: variant?.description,
    image: variant?.image,
    ...variantAttributes,
    mpn: variant?.sku,
    gtin: gtin12,
    gtin12: gtin12,
    offers: {
      '@type': 'Offer',
      url: variantUrl,
      priceCurrency: 'USD',
      price: currentPrice,
      priceValidUntil: priceValidUntil,
      itemCondition: 'https://schema.org/NewCondition',
      availability: 'https://schema.org/InStock',
      shippingDetails: {'@id': 'https://www.skaraudio.com/#shipping_policy'},
      hasMerchantReturnPolicy: {'@id': 'https://www.skaraudio.com/#return_policy'}
    }
  };

  if (variant?.image || variant?.image_1) {
    variantSchema.image = variant?.image ?? variant?.image_1;
  }

  if (reviewsAggregateCount > 0) {
    variantSchema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: reviewsAggregateAvgScore,
      reviewCount: reviewsAggregateCount
    };
  }
  // console.dir({variantSchema}, {depth: 3});
  // stop();
  return variantSchema;
};

const generateProductGroupJsonLd = (item, variantProducts) => {
  // console.dir({generateProductGroupData_item: item}, {depth: 2});
  let productGroupTitle = item?.name;
  let productGroupDescription = item?.description;
  let productGroupURL = item?.url;

  const firstProduct = item?.products?.[0];
  const firstProductSku = firstProduct?.sku;

  const isVehicleSpecific = skuIsLoadedOrUnloadedVehicleSpecificEnclosure(firstProductSku);
  if (isVehicleSpecific) {
    const firstProductTitleSplit = firstProduct?.name?.split('Compatible');
    productGroupTitle = firstProductTitleSplit?.[0].trim() ?? item?.name;
    productGroupDescription = convertVsDescriptionToUseAcrossProductGroupOfSimiliarVariants(firstProduct?.description);
    // console.log('[generateProductGroupJsonLd] productGroupDescription:', productGroupDescription);
  }

  const productGroupData = {
    '@context': 'https://schema.org/',
    '@type': 'ProductGroup',
    name: productGroupTitle,
    description: productGroupDescription || '',
    url: productGroupURL,
    brand: {
      '@type': 'Brand',
      name: 'Skar Audio'
    },
    productGroupID: item?.productGroupID,
    variesBy: item?.variesBy ?? null,
    hasVariant: variantProducts
  };

  if (item?.rating?.count > 0) {
    productGroupData.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: item?.rating?.avgRating,
      reviewCount: item?.rating?.count
    };
  }
  // console.log({productGroupData});
  return productGroupData;
};

const attachJsonLdShippingPolicyToJsonLdData = (jsonLdData) => {
  return [
    jsonLdData,
    {
      '@context': 'https://schema.org/',
      '@type': 'OfferShippingDetails',
      '@id': 'https://www.skaraudio.com/#shipping_policy',
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: 0.0,
        currency: 'USD'
      },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: 'US'
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
          '@type': 'QuantitativeValue',
          minValue: 0,
          maxValue: 1,
          unitCode: 'DAY'
        },
        transitTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 3,
          unitCode: 'DAY'
        }
      }
    }
  ];
};

export const attachJsonLdReturnPolicyToJsonLdData = (jsonLdData) => {
  return [
    ...jsonLdData,
    {
      '@context': 'https://schema.org/',
      '@type': 'MerchantReturnPolicy',
      '@id': 'https://www.skaraudio.com/#return_policy',
      applicableCountry: 'US',
      name: 'Skar Audio Return Policy',
      description: 'Skar Audio offers a 30-day return policy on all products purchased from our website. If you are not satisfied with your purchase, you may return it within 30 days of the purchase date for a full refund.',
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 30,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn'
    }
  ];
};

const generatePriceValidUntil = () => {
  const futureDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365 * 5);
  return convertJsDateToShortDate(futureDate);
};

const findValueByName = (jsonObject, name) => {
  if (jsonObject.variant_option_one_name === name) {
    return jsonObject.variant_option_one_value;
  } else if (jsonObject.variant_option_two_name === name) {
    return jsonObject.variant_option_two_value;
  } else if (jsonObject.variant_option_three_name === name) {
    return jsonObject.variant_option_three_value;
  } else {
    return null;
  }
};

const mapVariesByToGoogleSchemaName = {
  'Size': 'https://schema.org/size',
  'Finish': 'https://schema.org/material',
  'Style': 'https://schema.org/material',
  'Type': 'https://schema.org/pattern',
  'Coil Configuration': 'https://schema.org/pattern',
  'Color': 'https://schema.org/color'
};

const mapVariantOptionsToSchema = (product, variesBy) => {
  const result = {};

  if (variesBy?.length) {
    variesBy.forEach(item => {
      if (item) {
        const value = findValueByName(product, item);
        if (value) {
          const schemaUrl = mapVariesByToGoogleSchemaName[item];
          const schemaName = schemaUrl?.replace('https://schema.org/', '');
          result[schemaName] = value;
        }
      }
    });
  }
  return result;
};

export const convertVsDescriptionToUseAcrossProductGroupOfSimiliarVariants = (description) => {
  // Replace specific parts with generic placeholders and remove extra commas
  if (!description) return;
  const genericDescription = description.replace(/, (single|dual) [0-9]+-inch/, '')
    .replace(/, (ported|sealed)/, '')
    .replace(/, (down-firing|front-firing|armor coated)/, '');

  return genericDescription;
};
